/**
 * Which face of the day Today shows. Release 3 of 2.0 (docs/PRD-2.0.md §2).
 *
 * The clock decides, in the owner's own zone — the morning face until the
 * morning ends, the night face from the night starts, Now in between — and a
 * tap can override it for the session ("Start the day" leaves the morning;
 * "Close the day ›" on Now goes to the night early). The override is held in
 * sessionStorage so it survives a reload and dies with the tab: a face chosen
 * at 8pm must not still be chosen at 8am.
 *
 * AND IT DIES WITH THE CLOCK (15 Oct 2026). It was keyed on the day alone, and
 * the day runs 4am to 4am — so "Start the day" tapped at 9am was still winning
 * at 1am, and Nick had to close the day by hand. The override now also records
 * which face the clock showed when it was tapped, and holds only while the
 * clock still shows that one: once the clock would have changed the face
 * anyway, it does.
 */
export type Face = 'morning' | 'now' | 'night';

const KEY = 'adhd-me:face-override';

function minutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

export function faceByClock(now: Date, morningEndsAt: string, nightStartsAt: string): Face {
  const t = now.getHours() * 60 + now.getMinutes();
  // Before 4am is still the night of the day before: the app's own boundary.
  if (t < 4 * 60) return 'night';
  if (t < minutes(morningEndsAt)) return 'morning';
  if (t >= minutes(nightStartsAt)) return 'night';
  return 'now';
}

export interface FaceOverride {
  face: Face;
  /** The face the clock showed when this was chosen; the choice holds only while it still does. */
  from: Face;
}

export function readOverride(day: string): FaceOverride | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const { d, face, from } = JSON.parse(raw) as { d: string; face: Face; from?: Face };
    // An entry without `from` is from before 15 Oct 2026 and has no way to
    // say whether it still applies, so it does not.
    return d === day && from ? { face, from } : null;
  } catch {
    return null;
  }
}

/** Does a stored choice still apply, given what the clock shows now? */
export function overrideFace(stored: FaceOverride | null, byClock: Face): Face | null {
  return stored && stored.from === byClock ? stored.face : null;
}

export function writeOverride(day: string, face: Face | null, from: Face): void {
  try {
    if (face === null) sessionStorage.removeItem(KEY);
    else sessionStorage.setItem(KEY, JSON.stringify({ d: day, face, from }));
  } catch {
    /* a nicety */
  }
}
